import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import { css } from '@emotion/core'
import {
  CORNER_NAV_HEADER_TOP_CSS,
  CORNER_NAV_LEFT_CSS,
  CORNER_NAV_SIZE_CSS,
  CORNER_NAV_TOP_CSS,
  EDGE_TAB_PLACEMENT,
  NAV_SURFACE_BACKGROUND,
  NAV_SURFACE_COLOR,
  NAV_SURFACE_HOVER_BACKGROUND,
  NAV_SURFACE_SHADOW,
  NAV_SURFACE_Z_INDEX,
  NAV_TRIGGER_BLOCK_SIZE,
  NAV_TRIGGER_INLINE_SIZE,
  TOP_CORNER_PLACEMENT,
  focusRingCss,
} from './experienceNavSurface.styles'
import { experienceNavControlProps } from './experienceNavControlMarker'

// What both placements share: fixed over the experience on the card's
// surface, a centered glyph, and a hover/focus tint.
const triggerBaseCss = css`
  position: fixed;
  z-index: ${NAV_SURFACE_Z_INDEX};
  box-sizing: border-box;
  border: none;
  background: ${NAV_SURFACE_BACKGROUND};
  color: ${NAV_SURFACE_COLOR};
  box-shadow: ${NAV_SURFACE_SHADOW};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover,
  &:focus {
    background: ${NAV_SURFACE_HOVER_BACKGROUND};
  }

  // The background shift above is shared with hover, so on its own it does not tell a
  // keyboard user where they are - and the step buttons beside this one already draw
  // the ring, which made the trigger the one control on the surface without a visible
  // focus indicator (WCAG 2.4.7). Same ring as those, so the surface is consistent.
  ${focusRingCss};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

// One tab in every orientation, vertically centered so it lands under the
// same thumb on a 1080x1920 kiosk and a 1920x1080 one. It intentionally stays
// on the physical left edge in RTL, so `left` is deliberate here.
/* eslint-disable @fs/zion/logical-over-directional */
const edgeTabTriggerCss = css`
  ${triggerBaseCss};
  top: 50%;
  left: 0;
  width: ${NAV_TRIGGER_INLINE_SIZE};
  height: ${NAV_TRIGGER_BLOCK_SIZE};
  /* A semicircle's visual weight sits inboard of its bounding box, so the
     glyph is nudged back toward the flat edge to look centered in the bulge. */
  padding: 0 8px 0 4px;
  border-radius: 0 ${NAV_TRIGGER_INLINE_SIZE} ${NAV_TRIGGER_INLINE_SIZE} 0;
  transform: translateY(-50%);
  transition: background 0.2s ease;
`

// The WEB counterpart: a round button in the top start corner rather than a
// full-height-reachable edge tab, on the shared corner geometry the stepper
// mirrors across the screen.
const cornerTriggerCss = (headerVisible) => css`
  ${triggerBaseCss};
  top: ${headerVisible ? CORNER_NAV_HEADER_TOP_CSS : CORNER_NAV_TOP_CSS};
  left: ${CORNER_NAV_LEFT_CSS};
  width: ${CORNER_NAV_SIZE_CSS};
  height: ${CORNER_NAV_SIZE_CSS};
  padding: 0;
  border-radius: 50%;
  transition:
    transform 0.2s ease,
    background 0.2s ease;

  &:hover,
  &:focus {
    transform: scale(1.05);
  }
`
/* eslint-enable @fs/zion/logical-over-directional */

/**
 * The handle the navigation controls keep on screen, in either placement.
 * Usually it opens the card (ExperienceNavigationDrawer); where the card
 * would hold a single row it is that row (ExperienceNavSoleRowHandle). One
 * component so the two are the same control in the same place, whatever it
 * does when tapped.
 *
 * Anything a button takes - the click handler, the ARIA that says what it is
 * and does, a className carrying extra styles - passes straight through.
 */
const ExperienceNavTrigger = forwardRef(function ExperienceNavTrigger(
  { placement, headerVisible, children, ...buttonProps },
  ref
) {
  return (
    <button
      ref={ref}
      type="button"
      css={placement === EDGE_TAB_PLACEMENT ? edgeTabTriggerCss : cornerTriggerCss(headerVisible)}
      {...experienceNavControlProps}
      {...buttonProps}
    >
      {children}
    </button>
  )
})

ExperienceNavTrigger.propTypes = {
  placement: PropTypes.oneOf([EDGE_TAB_PLACEMENT, TOP_CORNER_PLACEMENT]).isRequired,
  headerVisible: PropTypes.bool.isRequired,
  /** The glyph; the button is icon-only, so callers must pass an aria-label. */
  children: PropTypes.node.isRequired,
}

export default ExperienceNavTrigger
